import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { INDUSTRIES } from "../lib/constants";
import { Button } from "../components/ui/button";

const FOCUS = [
  {
    title: "Banking & Financial Services",
    body: "Audits of commercial banks, MFIs, MTBs and emerging fintech players. AML/CFT framework reviews aligned with Central Bank of Somalia requirements.",
  },
  {
    title: "NGOs & Donor Programs",
    body: "Project and statutory audits compliant with USAID, EU, UN and FCDO requirements. Strong track‑record on donor compliance and procurement reviews.",
  },
  {
    title: "Telecommunications",
    body: "Revenue assurance, regulatory reporting and IFRS opinions for mobile operators, ISPs and digital infrastructure providers.",
  },
  {
    title: "Public Sector & SOEs",
    body: "Audits of ministries, state‑owned enterprises and donor‑funded programs under IPSAS, with attention to public‑finance governance.",
  },
];

export default function Industries() {
  return (
    <div data-testid="page-industries">
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-16">
          <div className="overline rule-teal">Industries</div>
          <h1 className="font-display text-5xl sm:text-6xl mt-6 leading-[1.02] text-[#0B2E5C] max-w-3xl">
            Sector depth that shows in the working papers.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#475569] leading-relaxed font-light">
            Industry experience matters in audit. Our teams arrive with the right
            risk lens, regulatory awareness and operational understanding for
            each sector we serve.
          </p>
        </div>
      </section>

      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]" data-testid="industries-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {INDUSTRIES.map((ind, i) => (
              <div
                key={ind.name}
                data-testid={`industry-card-${i}`}
                className="bg-white p-8 min-h-[200px] flex flex-col justify-between card-lift"
              >
                <div>
                  <span className="font-display text-2xl text-[#2BA8B8]">
                    0{i + 1}
                  </span>
                  <h3 className="font-display text-xl text-[#0B2E5C] mt-4 leading-snug">
                    {ind.name}
                  </h3>
                </div>
                <p className="text-sm text-[#475569] font-light mt-4">{ind.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white" data-testid="industries-focus">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="overline rule-teal">Focus areas</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight text-[#0B2E5C] max-w-3xl">
            Where our practice is deepest.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px mt-14 bg-[#E2E8F0] border border-[#E2E8F0]">
            {FOCUS.map((f, i) => (
              <div
                key={i}
                data-testid={`focus-${i}`}
                className="bg-white p-10 card-lift"
              >
                <h3 className="font-display text-2xl text-[#0B2E5C] leading-tight">
                  {f.title}
                </h3>
                <p className="mt-5 text-[#475569] font-light leading-relaxed">
                  {f.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F1F5F9]" data-testid="industries-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="overline rule-teal">Engage</div>
            <h2 className="font-display text-4xl mt-4 leading-tight text-[#0B2E5C]">
              Don't see your sector?
            </h2>
            <p className="mt-4 text-[#475569] font-light text-lg max-w-2xl">
              We frequently work outside this list. Tell us about your business
              and we'll let you know if we're the right fit.
            </p>
          </div>
          <div className="lg:col-span-4">
            <Link to="/contact" data-testid="industries-cta-contact">
              <Button className="w-full rounded-sm bg-[#0B2E5C] hover:bg-[#082142] text-white h-14 text-sm tracking-wide">
                Discuss your sector
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
